"use client";

import { Button, cn } from "@oreme/ui";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import React, { useReducer, useRef, type ReactElement } from "react";
import { flushSync } from "react-dom";
import Moveable from "react-moveable";
import { SitesMapSwitchProjection, SitesMapSwitchTile } from "@/components";
import { activePanelAtom, settingsVisibilityAtom } from "@/store/sites";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";

const sitesPanelSettingsVariants = cva(
  "pointer-events-auto absolute top-1/2 left-1/2 -ml-[160px] -mt-[182px] rounded-lg bg-background/90 hover:bg-background transition-background border p-3 shadow-lg drop-shadow-lg flex-col gap-6 w-[320px] animate__animated animate__fadeIn animate__faster",
  {
    variants: {
      active: {
        true: "z-20",
        false: "z-10",
      },
      visible: {
        true: "flex",
        false: "hidden",
      },
      dragged: {
        true: "z-30",
        false: "",
      },
    },
    defaultVariants: {
      active: false,
      visible: false,
      dragged: false,
    },
  },
);

function SitesPanelSettings(): ReactElement {
  const t = useTranslations("sites.PanelSettings");
  const [activePanel, setActivePanel] = useAtom(activePanelAtom);

  const targetRef = useRef<HTMLDivElement>(null);
  const dragTargetRef = useRef<HTMLButtonElement>(null);
  const moveableRef = useRef<Moveable>(null);

  const isActive = activePanel === SitesPanelSettings.name;
  const [isVisible, toggleVisibility] = useAtom(settingsVisibilityAtom);
  const [isDragged, toggleDraggedState] = useReducer((state) => {
    return !state;
  }, false);

  const handleMouseUp = (): void => {
    toggleDraggedState();
    setActivePanel(SitesPanelSettings.name);
  };

  return (
    <>
      <div
        id="tour-6"
        className={cn(
          sitesPanelSettingsVariants({
            active: isActive,
            visible: isVisible,
            dragged: isDragged,
          }),
        )}
        ref={targetRef}
      >
        <header className="flex flex-row justify-between items-center">
          <button
            className={`flex flex-row flex-1 hover:cursor-grab data-[state="dragged"]:cursor-grabbing`}
            data-state={isDragged ? "dragged" : "default"}
            onMouseDown={toggleDraggedState}
            onMouseUp={handleMouseUp}
            ref={dragTargetRef}
            type="button"
          >
            <h2 className="font-semibold">{t("title")}</h2>
          </button>
          <Button
            className="-mt-1 -mr-1"
            onClick={() => {
              toggleVisibility();
            }}
            variant="ghost"
            size="icon-sm"
            aria-label={t("close")}
          >
            <X className="h-4 w-4" />
          </Button>
        </header>
        <div className="flex flex-col gap-2">
          <h3 className="text-xs uppercase text-muted-foreground">
            {t("mapTile")}
          </h3>
          <ul className="list-none flex flex-col gap-1">
            <li className="flex gap-2 justify-between items-center">
              <SitesMapSwitchTile />
            </li>
          </ul>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="text-xs uppercase text-muted-foreground">
            {t("mapProjection")}
          </h3>
          <ul className="list-none flex flex-col gap-1">
            <li className="flex gap-2 justify-between items-center">
              <SitesMapSwitchProjection />
            </li>
          </ul>
        </div>
      </div>
      <Moveable
        dragTarget={dragTargetRef}
        draggable
        edge={false}
        flushSync={flushSync}
        hideDefaultLines
        onRender={(e) => {
          e.target.style.cssText += e.cssText;
        }}
        origin={false}
        ref={moveableRef}
        resizable={false}
        scalable={false}
        target={targetRef}
      />
    </>
  );
}

export { SitesPanelSettings };
